import React from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import TrackPill from "components/TrackPill"
import TalkTime from "components/TalkTime"
import theme from "theme"

import eye from "images/eye.svg"

export const pixelsPerMinute = "max(12px, calc(100vh / 90))"

const AgendaTalk = ({
  offsetMinutes,
  title,
  slug,
  track,
  time,
  duration,
  speakers,
  toBeAnnounced,
  className,
}) => (
  <div
    css={{
      position: "relative",
      background: theme.white,
      border: `2px solid ${theme.black}`,
      height: `calc(${pixelsPerMinute} * ${duration} + 2px)`,
      marginRight: -2,
      marginBottom: -2,
      marginTop: `calc(${pixelsPerMinute} * ${offsetMinutes})`,
      padding: 16,
      overflow: "hidden",
      [theme.tablet]: {
        height: "auto",
        margin: "0 0 -2px 0",
        borderLeft: 0,
        borderRight: 0,
        display: toBeAnnounced && "none",
      },
    }}
    className={className}
  >
    <h3
      css={{
        ...theme.hxs,
        marginBottom: 16,
        [theme.tablet]: {
          ...theme.hm,
        },
        [theme.mobile]: {
          ...theme.hs,
        },
      }}
    >
      {title}
    </h3>

    {!toBeAnnounced && (
      <>
        <div
          css={{
            [theme.tablet]: {
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            },
            [theme.mobile]: {
              display: "block",
            },
          }}
        >
          {speakers.map(({ _id, name, position }) => (
            <div key={_id} css={{ marginBottom: 8 }}>
              <h4 css={theme.hxxs}>{name}</h4>
              <div css={theme.bxs}>{position}</div>
            </div>
          ))}
        </div>

        <Link
          to={slug.current}
          css={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 6,
            outlineOffset: -1,
            ":hover + div": {
              "--linkTextDecoration": "underline",
            },
          }}
        />
      </>
    )}

    <div
      css={{
        position: "absolute",
        bottom: 0,
        background: theme.white,
        padding: "0 16px 16px",
        left: 0,
        right: 0,
        ":before": {
          content: "''",
          background:
            "linear-gradient(0deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
          position: "absolute",
          height: 64,
          bottom: "100%",
          left: 0,
          right: 0,
          zIndex: 4,
          pointerEvents: "none",
        },
        [theme.tablet]: {
          position: "static",
          padding: 0,
          marginTop: 32,
        },
      }}
    >
      {!toBeAnnounced && (
        <div css={{ display: "flex", marginBottom: 8 }}>
          {track && (
            <TrackPill
              {...track}
              tabIndex="-1"
              css={{ position: "relative", zIndex: 7 }}
            />
          )}
        </div>
      )}

      <TalkTime time={time} duration={duration} css={theme.hxxs} />

      {!toBeAnnounced && (
        <div
          css={{
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            css={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-start",
              fontWeight: 500,
            }}
          >
            <img
              src={eye}
              width={28}
              height={16}
              css={{ marginRight: 8 }}
              alt=""
            />
            <span
              css={{
                ...theme.hxxs,
                textDecoration: "var(--linkTextDecoration)",
                textDecorationThickness: 2,
              }}
            >
              See More
            </span>
          </div>
        </div>
      )}
    </div>
  </div>
)

export default AgendaTalk

AgendaTalk.propTypes = {
  offsetMinutes: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.shape({
    current: PropTypes.string.isRequired,
  }).isRequired,
  track: PropTypes.shape(TrackPill.propTypes),
  time: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  speakers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      position: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  toBeAnnounced: PropTypes.bool.isRequired,
  className: PropTypes.string,
}
